import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaArrowLeft,
  FaEdit,
  FaBuilding,
  FaEnvelope,
  FaCheckCircle,
  FaRegCopy,
  FaLock,
  FaKey,
  FaSearch,
  FaPrint,
  FaShieldAlt,
  FaFileContract,
  FaGavel,
  FaDatabase,
  FaUserCheck,
  FaExclamationTriangle,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaInfoCircle,
} from "react-icons/fa";
import BASE_URL from "../utils/Urls";
import KeypadModal from "../models/KeypadModal";

export const DEFAULT_TERMS_SECTIONS = [
  {
    id: "term-1",
    num: 1,
    title: "Wallet and Holding Amount After Suspension or Deactivation",
    icon: FaLock,
    category: "wallet",
    tag: "Financials",
    content: `1.1. If a user's account is suspended, permanently deactivated, or otherwise terminated because of a violation of the Platform's Terms, misuse, fraud, security concerns, or other applicable grounds, any amount remaining in the user's wallet or any amount placed on hold may be subject to forfeiture, to the extent permitted by applicable law.
1.2. If an account is subsequently reactivated, previously forfeited, removed, or otherwise unavailable wallet/holding amounts will not automatically be restored or credited to the account.
1.3. The Company may review exceptional cases, technical errors, unauthorized transactions, or legally required refunds separately.
1.4. Nothing in this clause removes any refund or other right that cannot legally be excluded.`,
  },
  {
    id: "term-2",
    num: 2,
    title: "Sectors, Prices and Services",
    icon: FaBuilding,
    category: "sectors",
    tag: "Sectors & Pricing",
    content: `2.1. The Company may introduce additional sectors in the future.
2.2. The Company may modify, rename, merge, suspend, remove, or discontinue sectors.
2.3. Sector names, service types, prices, discounts, eligibility requirements, required documents, verification requirements, and access conditions may change from time to time.
2.4. Users will be responsible for checking the current price, service type, requirements, and applicable conditions before making a payment or booking.
2.5. A previous price, discount, sector arrangement, or service condition does not create a permanent entitlement to the same price or service.`,
  },
  {
    id: "term-3",
    num: 3,
    title: "Verification and Uploaded Documents",
    icon: FaUserCheck,
    category: "verification",
    tag: "KYC & Verification",
    content: `3.1. The Platform may request selfies, photographs, work photographs, identity documents, qualification documents, experience documents, or other supporting information.
3.2. Verification may be performed manually, automatically, or through third-party verification technology.
3.3. Verification may take additional time depending on document quality, workload, technical conditions, security checks, or other circumstances.
3.4. Submission of a document or photograph does not mean that verification has been completed or approved.
3.5. Users must upload genuine and accurate information.
3.6. Uploading false, misleading, manipulated, unrelated, or fraudulent information may result in rejection, restriction, suspension, or deactivation of the account.`,
  },
  {
    id: "term-4",
    num: 4,
    title: "Gallery and Photograph Uploads",
    icon: FaDatabase,
    category: "media",
    tag: "Media & Photos",
    content: `4.1. Where the Platform allows users to upload photographs from their device gallery, users must ensure that the photographs are relevant to the requested purpose.
4.2. Users must not upload photographs merely to falsely indicate attendance, absence, work completion, work performance, location, identity, or other facts.
4.3. Users must not upload content intended to mislead the Company or another user.
4.4. False or misleading uploads may result in account suspension or deactivation.
4.5. The Platform may determine where uploaded content is displayed or categorized based on the applicable Platform workflow.`,
  },
  {
    id: "term-5",
    num: 5,
    title: "Work Details Across Sectors",
    icon: FaFileContract,
    category: "sectors",
    tag: "Sector Operations",
    content: `5.1. The Company may allow authorized Platform personnel or administrators to add, modify, organize, or update work/service details across one or more sectors.
5.2. Users acknowledge that work information may be updated, corrected, moved, reorganized, or displayed differently as the Platform develops.`,
  },
  {
    id: "term-6",
    num: 6,
    title: "Location Confirmation",
    icon: FaShieldAlt,
    category: "location",
    tag: "Safety & Location",
    content: `6.1. Before starting navigation or travelling to a work location, users should communicate with the relevant user and confirm the location.
6.2. Users must confirm the actual address, landmark, door number, phone number, or other relevant location information before proceeding.
6.3. Users acknowledge that another user may provide an inaccurate, incomplete, outdated, or incorrect location.
6.4. The Company only provides location/navigation functionality as a convenience and does not guarantee that the location supplied by another user is accurate.
6.5. Users should not begin travel solely on the basis of an unconfirmed map location.`,
  },
  {
    id: "term-7",
    num: 7,
    title: "User Confirmation of Price, Service and Identity",
    icon: FaUserCheck,
    category: "conduct",
    tag: "User Due Diligence",
    content: `7.1. Users are responsible for confirming the identity of the other party before proceeding.
7.2. Users are responsible for confirming the applicable price, work/service type, scope of work, timing, location, and other important details.
7.3. The Company provides the Platform with information supplied or displayed through the system but does not independently guarantee every detail provided by users.`,
  },
  {
    id: "term-8",
    num: 8,
    title: "Platform's Limited Role",
    icon: FaBuilding,
    category: "legal",
    tag: "Platform Role",
    content: `8.1. The Company primarily provides technology, connection, location, service-type, booking, communication, and related Platform functionality.
8.2. Unless expressly stated otherwise, the Company does not directly provide the work or service arranged between users.
8.3. The Company does not guarantee the conduct, reliability, qualifications, identity, performance, payment, attendance, safety, or behaviour of another user.
8.4. Users remain responsible for exercising reasonable care and conducting their own verification before proceeding with another user.
8.5. Nothing in these Terms excludes responsibility that cannot legally be excluded.`,
  },
  {
    id: "term-9",
    num: 9,
    title: "Future Changes to Charges",
    icon: FaLock,
    category: "pricing",
    tag: "Fees & Pricing",
    content: `9.1. The Company may change Platform fees, sector access charges, service charges, booking charges, discounts, or other charges in the future.
9.2. The applicable charge will be displayed to the user before the relevant transaction wherever required.
9.3. A user is responsible for reviewing the amount shown before confirming a transaction.`,
  },
  {
    id: "term-10",
    num: 10,
    title: "Future Payment Model",
    icon: FaLock,
    category: "pricing",
    tag: "Revenue Model",
    content: `10.1. The Company currently may operate using Platform fees or other disclosed charges.
10.2. The Company may introduce, modify, or replace its revenue model in the future, including introducing commission-based charges or other lawful charging models.
10.3. Any applicable charge will be communicated or displayed in accordance with applicable law and Platform requirements.`,
  },
  {
    id: "term-11",
    num: 11,
    title: "Temporary Service Interruptions",
    icon: FaExclamationTriangle,
    category: "technical",
    tag: "Uptime & Maintenance",
    content: `11.1. Some Platform services may temporarily become unavailable because of:
• Technical problems;
• Maintenance;
• Server problems;
• Network problems;
• Security incidents;
• Third-party service failures;
• Updates;
• Legal or regulatory requirements;
• Operational reasons;
• Other circumstances beyond the Company's reasonable control.
11.2. Users are expected to cooperate with reasonable temporary interruptions.
11.3. The Company will make reasonable efforts to restore affected services as soon as reasonably practicable.`,
  },
  {
    id: "term-12",
    num: 12,
    title: "Device and Login Changes",
    icon: FaShieldAlt,
    category: "security",
    tag: "Authentication",
    content: `12.1. The Platform may restrict simultaneous logins or maintain a single active session depending on the security design of the Platform.
12.2. Logging into another device may cause a previous device session to be logged out.
12.3. Users are responsible for keeping their account secure and must not intentionally share account credentials.`,
  },
  {
    id: "term-13",
    num: 13,
    title: "Device Switching and Technical Data Issues",
    icon: FaDatabase,
    category: "technical",
    tag: "Data Sync",
    content: `13.1. When a user changes devices, reinstalls the application, changes operating systems, or experiences technical problems, certain locally stored information, temporary information, drafts, cached information, or other data may not appear exactly as previously displayed.
13.2. The Company will make reasonable efforts to maintain server-side account information.
13.3. However, the Company cannot guarantee that every temporary, cached, device-specific, or technically affected item will always be recoverable.
13.4. Users should maintain important records of bookings, payments, communications, and other important information where appropriate.`,
  },
  {
    id: "term-14",
    num: 14,
    title: "Respectful Behaviour & Community Conduct",
    icon: FaGavel,
    category: "conduct",
    tag: "Community Standards",
    content: `14.1. Users must communicate respectfully with other users and Platform staff.
14.2. Abusive, threatening, insulting, discriminatory, harassing, or inappropriate behaviour may result in restrictions, suspension, or deactivation.
14.3. Users should submit complaints through the designated support channels rather than abusing Platform personnel.`,
  },
  {
    id: "term-15",
    num: 15,
    title: "OTP Limits and Security Controls",
    icon: FaLock,
    category: "security",
    tag: "OTP Security",
    content: `15.1. The Platform may impose daily, hourly, device-based, account-based, or other OTP/request limits for security and fraud prevention.
15.2. Users should use OTP functionality responsibly.
15.3. Excessive or suspicious OTP requests may temporarily restrict further requests.
15.4. The Company is not responsible for inconvenience caused by security-related OTP restrictions, subject to applicable law.`,
  },
  {
    id: "term-16",
    num: 16,
    title: "Platform Updates and UI Changes",
    icon: FaEdit,
    category: "technical",
    tag: "Platform Updates",
    content: `16.1. The Company may update the Platform's design, user interface, navigation, workflows, features, buttons, menus, and processes.
16.2. Users agree that the Platform may look or function differently following updates.
16.3. Continued use of the Platform after an update may require users to follow the updated workflow.`,
  },
  {
    id: "term-17",
    num: 17,
    title: "Support Delays and Response Times",
    icon: FaEnvelope,
    category: "support",
    tag: "Help Desk",
    content: `17.1. Customer support may experience delays because of high request volumes, technical issues, holidays, investigations, third-party dependencies, or other circumstances.
17.2. The Company will make reasonable efforts to respond to support requests.
17.3. Users should provide complete information when submitting a support ticket or email so that the issue can be investigated efficiently.`,
  },
  {
    id: "term-18",
    num: 18,
    title: "Holding Amount Release Delays",
    icon: FaLock,
    category: "wallet",
    tag: "Holding Settlements",
    content: `18.1. A holding amount may occasionally take additional time to release because of technical processing, verification, disputes, payment-provider processing, investigation, or other operational reasons.
18.2. Users may raise a support ticket or contact the Company by email if a holding amount has not been released within the applicable stated period.
18.3. The Company will make reasonable efforts to investigate and resolve the issue.`,
  },
  {
    id: "term-19",
    num: 19,
    title: "Coupon Codes, Super Incentives and Discounts",
    icon: FaCheckCircle,
    category: "wallet",
    tag: "Promotions & Coupons",
    content: `19.1. Coupons, promotional codes, and discounts may be subject to specific conditions, expiry dates, usage limits, eligibility requirements, or sector restrictions.
19.2. If a user fails to apply an eligible coupon before completing the relevant transaction, the coupon may not be available for later application or re-use.
19.3. Unless expressly stated otherwise, the Company is not required to retroactively apply a coupon to a completed transaction.`,
  },
  {
    id: "term-20",
    num: 20,
    title: "False Attendance or Misleading Photographs",
    icon: FaGavel,
    category: "conduct",
    tag: "Anti-Fraud Policy",
    content: `20.1. Users must not upload photographs or other content falsely representing their attendance, absence, work completion, location, identity, or other material facts.
20.2. Deliberately misleading uploads may be treated as misuse of the Platform.
20.3. Depending on the seriousness of the conduct, the Company may reject the content, cancel the relevant transaction, restrict the account, suspend the account, or permanently deactivate the account.`,
  },
  {
    id: "term-21",
    num: 21,
    title: "Bugs and Suggestions",
    icon: FaEdit,
    category: "technical",
    tag: "Bug Reports",
    content: `21.1. Users are encouraged to report bugs, technical issues, security concerns, and suggestions through the available support channels.
21.2. The Company may review and implement suggestions at its discretion.
21.3. A reward may occasionally be offered for a particularly useful bug report or suggestion, but no reward is guaranteed unless expressly announced by the Company.`,
  },
  {
    id: "term-22",
    num: 22,
    title: "Platform Intellectual Property",
    icon: FaShieldAlt,
    category: "legal",
    tag: "Copyright & IP",
    content: `22.1. The Platform's software, user interface, workflows, system architecture, graphics, branding, logos, original content, databases, and other proprietary materials belong to the Company or its licensors, subject to applicable rights.
22.2. Users must not copy, reproduce, replicate, reverse engineer, scrape, reproduce, or commercially exploit the Platform's proprietary workflows, designs, content, or systems without authorization.
22.3. The Company may take legal action where a person unlawfully copies or misuses protected intellectual property or confidential/proprietary materials.`,
  },
  {
    id: "term-23",
    num: 23,
    title: "Company Development and Future Growth",
    icon: FaBuilding,
    category: "legal",
    tag: "Corporate Governance",
    content: `23.1. The Company may seek investors, grants, funding, partnerships, strategic investments, or other forms of business development.
23.2. Such activities do not change the user's rights or obligations under the Platform Terms.`,
  },
  {
    id: "term-24",
    num: 24,
    title: "Initial-Stage Platform Development",
    icon: FaFileContract,
    category: "technical",
    tag: "Development Phase",
    content: `24.1. As the Platform continues to develop, certain features may be improved, corrected, modified, or temporarily unavailable.
24.2. Users acknowledge that the Company will make reasonable efforts to identify and resolve bugs and technical problems.
24.3. Users are encouraged to report problems through the official support channels.
24.4. Nothing in this clause removes any legal rights available to users under applicable law.`,
  },
  {
    id: "term-25",
    num: 25,
    title: "Future Logo, Branding and Terms Changes",
    icon: FaEdit,
    category: "legal",
    tag: "Terms Amendments",
    content: `25.1. The Company may update its logo, branding, Terms & Conditions, Privacy Policy, user interface, services, sectors, pricing, and operational processes from time to time.
25.2. Updated terms will be made available through appropriate Platform channels.`,
  },
  {
    id: "term-26",
    num: 26,
    title: "Account Data and Technical Issues",
    icon: FaDatabase,
    category: "technical",
    tag: "Data Integrity",
    content: `26.1. The Company will take reasonable measures to protect and maintain account data.
26.2. However, temporary technical failures, synchronization problems, device issues, network failures, software bugs, or other technical circumstances may affect how information is displayed.
26.3. The Company will make reasonable efforts to correct confirmed technical errors.`,
  },
  {
    id: "term-27",
    num: 27,
    title: "Support Inconsistencies & Display Discrepancies",
    icon: FaEnvelope,
    category: "support",
    tag: "Verification Desk",
    content: `27.1. Users should contact support when they identify missing, incorrect, or inconsistent information rather than assuming that the displayed information is final.`,
  },
];

export default function MobileTerms() {
  const navigate = useNavigate();
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSections, setExpandedSections] = useState({});

  const [editingTerm, setEditingTerm] = useState(null);
  const [newContent, setNewContent] = useState("");
  const [saving, setSaving] = useState(false);

  const [showKeypad, setShowKeypad] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/admin/terms`);
        if (res.data?.result && res.data.result.length > 0) {
          // Look for row with id === 2 or terms content
          const termsRow = res.data.result.find(
            (r) => r.id === 2 || (r.content && (r.content.includes("ADDITIONAL USER TERMS") || r.content.includes("Wallet and Holding")))
          );

          if (termsRow) {
            setTerms([termsRow]);
          } else if (res.data.result.length > 1) {
            setTerms([res.data.result[1]]);
          } else {
            // Server only returned Privacy row, so use Terms fallback
            setTerms([{ id: 2, content: buildFullFallbackText(), updated_at: new Date().toISOString() }]);
          }
        } else {
          setTerms([{ id: 2, content: buildFullFallbackText(), updated_at: new Date().toISOString() }]);
        }
        setError(null);
      } catch (err) {
        console.warn("Could not fetch server terms, using fallback:", err.message);
        setTerms([{ id: 2, content: buildFullFallbackText(), updated_at: new Date().toISOString() }]);
      } finally {
        setLoading(false);
      }
    };
    fetchTerms();
  }, []);

  function buildFullFallbackText() {
    const preamble = "ADDITIONAL USER TERMS — PLATFORM-SPECIFIC RULES\nThese provisions supplement the User Terms & Conditions of ADDA FARM.\n\n";
    const clauses = DEFAULT_TERMS_SECTIONS.map((sec) => `${sec.num}. ${sec.title}\n${sec.content}`).join("\n\n");
    return preamble + clauses;
  }

  // Parse structured policy/terms data
  const parsedTermsData = useMemo(() => {
    if (!terms || terms.length === 0 || !terms[0]?.content) {
      return { preamble: "These provisions supplement the User Terms & Conditions of ADDA FARM.", sections: DEFAULT_TERMS_SECTIONS };
    }
    const rawText = terms[0].content;

    let preamble = "";
    let bodyText = rawText;
    const firstClauseIdx = rawText.search(/(?:^|\n)\d+\.\s+/);
    if (firstClauseIdx > 0) {
      preamble = rawText.slice(0, firstClauseIdx).trim();
      bodyText = rawText.slice(firstClauseIdx).trim();
    }

    const splitRegex = /(?=\n\d+\.\s+|^1\.\s+)/g;
    const chunks = bodyText.split(splitRegex).map((c) => c.trim()).filter(Boolean);

    if (chunks.length === 0) {
      return { preamble, sections: DEFAULT_TERMS_SECTIONS };
    }

    const sections = chunks.map((chunk, idx) => {
      const match = chunk.match(/^(\d+)\.\s*([^\n]+)\n?([\s\S]*)$/);
      const num = match ? parseInt(match[1], 10) || idx + 1 : idx + 1;
      const title = match ? match[2].trim() : `Clause ${idx + 1}`;
      const rawContent = match ? match[3].trim() : chunk;

      const defaultMatch = DEFAULT_TERMS_SECTIONS.find((s) => s.num === num);

      // Split subparts (e.g. 1.1, 1.2, 1.3)
      const subParts = rawContent.split(/(?=\n\d+\.\d+\.?\s+)/g);
      const subBlocks = subParts.map((part, pIdx) => {
        const pTrimmed = part.trim();
        const subMatch = pTrimmed.match(/^(\d+\.\d+)\.?\s*([^\n]*)\n?([\s\S]*)$/);
        if (subMatch) {
          const subNum = subMatch[1];
          const subTitle = subMatch[2].trim();
          const subContent = subMatch[3].trim();

          const lines = (subTitle ? subTitle + "\n" + subContent : subContent)
            .split("\n")
            .map((l) => l.trim())
            .filter(Boolean);

          const bullets = [];
          const introLines = [];

          lines.forEach((line) => {
            if (line.startsWith("•") || line.startsWith("-") || line.startsWith("*")) {
              bullets.push(line.replace(/^[•\-*]\s*/, "").replace(/;$/, ""));
            } else {
              introLines.push(line);
            }
          });

          return {
            id: `sub-${num}-${pIdx}`,
            subNum,
            subTitle: subTitle || null,
            intro: introLines.join(" "),
            bullets,
            raw: pTrimmed,
          };
        } else {
          const lines = pTrimmed.split("\n").map((l) => l.trim()).filter(Boolean);
          const bullets = [];
          const introLines = [];

          lines.forEach((line) => {
            if (line.startsWith("•") || line.startsWith("-") || line.startsWith("*")) {
              bullets.push(line.replace(/^[•\-*]\s*/, "").replace(/;$/, ""));
            } else {
              introLines.push(line);
            }
          });

          return {
            id: `sub-${num}-${pIdx}`,
            subNum: null,
            subTitle: null,
            intro: introLines.join("\n"),
            bullets,
            raw: pTrimmed,
          };
        }
      });

      return {
        id: `term-${num}`,
        num,
        title: title.charAt(0).toUpperCase() + title.slice(1),
        icon: defaultMatch?.icon || FaFileContract,
        category: defaultMatch?.category || "general",
        tag: defaultMatch?.tag || `Rule ${num}`,
        rawContent,
        subBlocks,
      };
    });

    return { preamble, sections };
  }, [terms]);

  // Filter sections by search query and category
  const filteredSections = useMemo(() => {
    return parsedTermsData.sections.filter((sec) => {
      const matchesCat =
        activeCategory === "all" ||
        sec.category === activeCategory ||
        (activeCategory === "wallet" && (sec.category === "pricing" || sec.category === "wallet")) ||
        (activeCategory === "security" && sec.category === "verification");
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        sec.title.toLowerCase().includes(q) ||
        sec.rawContent.toLowerCase().includes(q) ||
        sec.tag.toLowerCase().includes(q);
      return matchesCat && matchesSearch;
    });
  }, [parsedTermsData, activeCategory, searchQuery]);

  const toggleSection = (id) => {
    setExpandedSections((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const expandAll = () => {
    const all = {};
    parsedTermsData.sections.forEach((s) => {
      all[s.id] = true;
    });
    setExpandedSections(all);
  };

  const collapseAll = () => {
    setExpandedSections({});
  };

  const startEdit = (term) => {
    setEditingTerm(term);
    setNewContent(term.content);
    setUnlocked(false);
  };

  const unlockEdit = () => {
    setUnlocked(true);
    setShowKeypad(false);
  };

  const saveEdit = async () => {
    if (!unlocked) {
      setShowKeypad(true);
      return;
    }

    try {
      setSaving(true);
      await axios.put(`${BASE_URL}/admin/terms/${editingTerm.id}`, {
        content: newContent,
      });

      setTerms((prev) =>
        prev.map((t) =>
          t.id === editingTerm.id
            ? { ...t, content: newContent, updated_at: new Date().toISOString() }
            : t
        )
      );

      setEditingTerm(null);
      setNewContent("");
    } catch (err) {
      alert(err.response?.data?.message || "Saved locally");
      setTerms((prev) =>
        prev.map((t) =>
          t.id === editingTerm.id
            ? { ...t, content: newContent, updated_at: new Date().toISOString() }
            : t
        )
      );
      setEditingTerm(null);
      setNewContent("");
    } finally {
      setSaving(false);
    }
  };

  const copyEmail = () => {
    navigator.clipboard.writeText("support@adda-farm.com");
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2200);
  };

  const copyPageLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2200);
  };

  const handlePrint = () => {
    window.print();
  };

  const lastUpdated = terms[0]?.updated_at
    ? new Date(terms[0].updated_at).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "August 2026";

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800 font-sans pb-16">
      {/* ── Top Header Navigation Bar ────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs sm:text-sm font-semibold transition-all shadow-sm active:scale-95"
            >
              <FaArrowLeft className="text-xs" />
              <span>Dashboard</span>
            </button>
            <div className="h-5 w-[1px] bg-slate-300 hidden sm:block"></div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="text-xs sm:text-sm font-bold text-slate-900 tracking-tight">
                Adda Farm <span className="text-blue-600">Legal Portal</span>
              </span>
            </div>
          </div>

          {/* Navigation Segmented Switch */}
          <div className="hidden sm:flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs font-bold">
            <button
              onClick={() => navigate("/privacy")}
              className="px-3.5 py-1.5 rounded-lg text-slate-600 hover:text-slate-900 flex items-center gap-1.5 transition-all"
            >
              <FaShieldAlt className="text-xs" />
              <span>Privacy Policy</span>
            </button>
            <button
              onClick={() => navigate("/terms")}
              className="px-3.5 py-1.5 rounded-lg bg-white text-blue-600 shadow-sm flex items-center gap-1.5 transition-all"
            >
              <FaFileContract className="text-xs" />
              <span>Terms & Conditions</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={copyPageLink}
              title="Copy link"
              className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg text-sm transition-colors"
            >
              {copiedLink ? <FaCheckCircle className="text-emerald-500" /> : <FaRegCopy />}
            </button>
            <button
              onClick={handlePrint}
              title="Print Terms"
              className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg text-sm transition-colors hidden sm:block"
            >
              <FaPrint />
            </button>
            {terms[0] && (
              <button
                onClick={() => startEdit(terms[0])}
                className="flex items-center gap-1.5 px-3.5 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg text-xs sm:text-sm font-bold shadow-sm hover:shadow transition-all active:scale-95"
              >
                <FaEdit className="text-xs" />
                <span>Edit Terms</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ── Brand Hero Banner ────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-b from-slate-900 via-slate-900 to-indigo-950 text-white pt-10 pb-16 px-4 sm:px-6 lg:px-8 border-b border-indigo-950">
        <div className="max-w-5xl mx-auto text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-bold tracking-wider uppercase backdrop-blur-sm">
            <FaShieldAlt className="text-blue-400" />
            <span>Platform-Specific User Rules</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white">
            User Terms & Platform Rules
          </h1>

          <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto leading-relaxed">
            These supplemental provisions govern wallet handling, sector pricing, document verification, anti-fraud rules, and operational protocols across all Adda Farm services.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3 pt-2 text-xs font-semibold text-slate-300">
            <span className="flex items-center gap-1.5 bg-slate-800/80 px-3 py-1.5 rounded-lg border border-slate-700">
              <FaCheckCircle className="text-emerald-400" /> 27 Active Clauses
            </span>
            <span className="flex items-center gap-1.5 bg-slate-800/80 px-3 py-1.5 rounded-lg border border-slate-700">
              <FaShieldAlt className="text-blue-400" /> Statutory Compliance
            </span>
            <span className="flex items-center gap-1.5 bg-slate-800/80 px-3 py-1.5 rounded-lg border border-slate-700">
              <FaFileContract className="text-amber-400" /> Last Revised: {lastUpdated}
            </span>
          </div>

          {/* Key Metric Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-6 max-w-4xl mx-auto">
            <div className="bg-slate-800/60 backdrop-blur-sm p-4 rounded-2xl border border-slate-700/60 text-left">
              <div className="text-amber-400 text-base mb-1 font-black">27 Rules</div>
              <div className="text-xs font-bold text-white">Platform Rules</div>
              <div className="text-[11px] text-slate-400">Comprehensive framework</div>
            </div>
            <div className="bg-slate-800/60 backdrop-blur-sm p-4 rounded-2xl border border-slate-700/60 text-left">
              <div className="text-blue-400 text-base mb-1 font-black">KYC Gate</div>
              <div className="text-xs font-bold text-white">Strict Verification</div>
              <div className="text-[11px] text-slate-400">Document authenticity</div>
            </div>
            <div className="bg-slate-800/60 backdrop-blur-sm p-4 rounded-2xl border border-slate-700/60 text-left">
              <div className="text-emerald-400 text-base mb-1 font-black">Zero Fraud</div>
              <div className="text-xs font-bold text-white">Anti-Misleading</div>
              <div className="text-[11px] text-slate-400">Enforced forfeiture</div>
            </div>
            <div className="bg-slate-800/60 backdrop-blur-sm p-4 rounded-2xl border border-slate-700/60 text-left">
              <div className="text-indigo-400 text-base mb-1 font-black">Direct Desk</div>
              <div className="text-xs font-bold text-white">Help Center</div>
              <div className="text-[11px] text-slate-400">24–48h response</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Main Container ──────────────────────────────────────────────────── */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        {/* ── Search & Filter Bar ───────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-lg border border-slate-200 mb-8 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            {/* Search Input */}
            <div className="relative flex-1">
              <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                placeholder="Search across 27 rules (e.g. wallet, holding, sector, OTP, photos)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                >
                  <FaTimes className="text-xs" />
                </button>
              )}
            </div>

            {/* Expand / Collapse Controls */}
            <div className="flex items-center gap-2 self-end sm:self-auto">
              <button
                onClick={expandAll}
                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all"
              >
                Collapse All
              </button>
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 pt-1 scrollbar-none text-xs">
            {[
              { id: "all", label: "All 27 Rules" },
              { id: "wallet", label: "Wallet & Pricing" },
              { id: "verification", label: "KYC & Documents" },
              { id: "sectors", label: "Sectors & Operations" },
              { id: "conduct", label: "Code of Conduct" },
              { id: "technical", label: "Technical & Data" },
              { id: "legal", label: "Legal & IP" },
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-3 py-1.5 rounded-lg font-bold whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Policy Preamble Notice ──────────────────────────────────────── */}
        {parsedTermsData.preamble && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-5 flex items-start gap-3.5 shadow-sm">
            <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0 text-sm shadow-sm mt-0.5">
              <FaInfoCircle />
            </div>
            <div>
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-blue-800 bg-blue-100 px-2 py-0.5 rounded-md inline-block mb-1">
                Supplemental Legal Rules
              </span>
              <p className="text-sm font-semibold text-slate-800 leading-relaxed">
                {parsedTermsData.preamble}
              </p>
            </div>
          </div>
        )}

        {/* ── Policy Sections List ─────────────────────────────────────────── */}
        {loading ? (
          <div className="bg-white rounded-2xl p-16 border border-slate-200 text-center shadow-sm">
            <div className="inline-block w-9 h-9 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-slate-600 font-semibold">Loading platform terms and rules...</p>
          </div>
        ) : error ? (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 p-6 rounded-2xl mb-6 text-center">
            <FaExclamationTriangle className="inline-block text-xl mr-2 mb-1" />
            <span className="font-semibold">{error}</span>
          </div>
        ) : filteredSections.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 border border-slate-200 text-center">
            <FaInfoCircle className="text-3xl text-slate-400 mx-auto mb-3" />
            <h3 className="text-base font-bold text-slate-800 mb-1">No matching rules found</h3>
            <p className="text-xs text-slate-500">
              No terms matched "{searchQuery}". Try searching for keywords like "wallet", "sector", or "verification".
            </p>
            <button
              onClick={() => {
                setSearchQuery("");
                setActiveCategory("all");
              }}
              className="mt-4 px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSections.map((sec) => {
              const Icon = sec.icon || FaFileContract;
              const isExpanded = expandedSections[sec.id] !== false; // Default expanded

              return (
                <div
                  key={sec.id}
                  id={sec.id}
                  className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:border-slate-300 transition-all overflow-hidden"
                >
                  {/* Section Accordion Header */}
                  <div
                    onClick={() => toggleSection(sec.id)}
                    className="p-5 sm:p-6 flex items-start sm:items-center justify-between gap-4 cursor-pointer select-none bg-white hover:bg-slate-50/70 transition-colors"
                  >
                    <div className="flex items-start sm:items-center gap-3.5">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center text-lg flex-shrink-0">
                        <Icon />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-extrabold bg-slate-100 text-slate-700">
                            Rule {sec.num}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-blue-50 text-blue-700">
                            {sec.tag}
                          </span>
                        </div>
                        <h2 className="text-base sm:text-lg font-bold text-slate-900">
                          {sec.title}
                        </h2>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-slate-400">
                      <span className="text-xs font-semibold hidden md:block">
                        {isExpanded ? "Collapse" : "Read More"}
                      </span>
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                        {isExpanded ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />}
                      </div>
                    </div>
                  </div>

                  {/* Section Expanded Body */}
                  {isExpanded && (
                    <div className="px-5 sm:px-6 pb-6 pt-3 border-t border-slate-100 space-y-3">
                      {sec.subBlocks && sec.subBlocks.length > 0 ? (
                        sec.subBlocks.map((sub, sIdx) => {
                          if (sub.subNum) {
                            return (
                              <div
                                key={sIdx}
                                className="bg-slate-50/90 border border-slate-200/90 rounded-xl p-4 sm:p-5 shadow-xs"
                              >
                                <div className="flex items-start gap-3">
                                  <span className="px-2 py-0.5 bg-blue-600 text-white rounded text-xs font-black flex-shrink-0 mt-0.5">
                                    {sub.subNum}
                                  </span>
                                  <div className="space-y-2 flex-1">
                                    {sub.subTitle && (
                                      <h3 className="font-bold text-slate-900 text-sm">
                                        {sub.subTitle}
                                      </h3>
                                    )}
                                    {sub.intro && (
                                      <p className="text-xs sm:text-sm text-slate-800 leading-relaxed font-normal">
                                        {sub.intro}
                                      </p>
                                    )}
                                    {sub.bullets && sub.bullets.length > 0 && (
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                                        {sub.bullets.map((b, bIdx) => (
                                          <div
                                            key={bIdx}
                                            className="flex items-start gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200/70 shadow-xs"
                                          >
                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></span>
                                            <span className="text-xs sm:text-sm text-slate-800 font-medium leading-relaxed">
                                              {b}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          // General block
                          return (
                            <div key={sIdx} className="space-y-3">
                              {sub.intro && (
                                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line font-medium">
                                  {sub.intro}
                                </p>
                              )}
                              {sub.bullets && sub.bullets.length > 0 && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {sub.bullets.map((b, bIdx) => (
                                    <div
                                      key={bIdx}
                                      className="flex items-start gap-2 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200/70"
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2 flex-shrink-0"></span>
                                      <span className="text-xs sm:text-sm text-slate-800 font-medium leading-relaxed">
                                        {b}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm leading-relaxed whitespace-pre-line font-normal text-slate-700">
                          {sec.rawContent}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Official Compliance & Grievance Contact Card ──────────────────── */}
        <div className="mt-10 bg-gradient-to-br from-slate-900 via-indigo-950 to-blue-950 text-white rounded-3xl p-6 sm:p-10 shadow-xl border border-indigo-800">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="space-y-3 max-w-2xl">
              <div className="flex items-center gap-2 text-blue-300 text-xs font-extrabold uppercase tracking-widest">
                <FaBuilding className="text-sm" />
                <span>Adda Farm Legal & Compliance Desk</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                Terms Inquiries & Support Help Desk
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                For questions regarding wallet holds, sector additions, document verifications, or dispute resolutions, please reach out directly to our support desk.
              </p>
              <div className="pt-2 flex flex-wrap items-center gap-4 text-xs text-slate-300">
                <span className="flex items-center gap-1.5">
                  <FaCheckCircle className="text-emerald-400" /> Grievance Officer
                </span>
                <span className="flex items-center gap-1.5">
                  <FaShieldAlt className="text-blue-400" /> Dispute Resolution Desk
                </span>
              </div>
            </div>

            {/* Direct Action Contact Button */}
            <div className="bg-slate-800/80 backdrop-blur-md border border-indigo-500/30 p-6 rounded-2xl flex flex-col items-center gap-4 w-full lg:w-auto min-w-[280px]">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xl">
                <FaEnvelope />
              </div>
              <div className="text-center">
                <div className="text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">
                  Direct Support Email
                </div>
                <div className="text-sm font-black text-white flex items-center justify-center gap-2">
                  <span>support@adda-farm.com</span>
                  <button
                    onClick={copyEmail}
                    className="p-1 hover:text-blue-400 transition-colors"
                    title="Copy Email"
                  >
                    {copiedEmail ? <FaCheckCircle className="text-emerald-400" /> : <FaRegCopy />}
                  </button>
                </div>
              </div>

              <a
                href="mailto:support@adda-farm.com"
                className="w-full py-2.5 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl text-center text-xs font-bold shadow-md transition-all active:scale-95"
              >
                Email Support Desk
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* ── Edit Modal with Keypad Protection ────────────────────────────────── */}
      {editingTerm && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full p-6 sm:p-8 space-y-5 border border-slate-200 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-lg font-bold">
                  <FaEdit />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900">
                    Edit Terms & Conditions
                  </h3>
                  <p className="text-xs text-slate-500">
                    Requires Secondary Administrator Lock PIN to save changes
                  </p>
                </div>
              </div>

              <button
                onClick={() => setEditingTerm(null)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-100"
              >
                <FaTimes />
              </button>
            </div>

            {/* Lock Status Pill */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 rounded-xl border border-slate-200 text-xs">
              <span className="font-semibold text-slate-700">Security Authorization:</span>
              {unlocked ? (
                <span className="inline-flex items-center gap-1.5 font-bold text-emerald-700 bg-emerald-100/80 px-2.5 py-1 rounded-lg">
                  <FaCheckCircle /> PIN Verified — Unlocked
                </span>
              ) : (
                <button
                  onClick={() => setShowKeypad(true)}
                  className="inline-flex items-center gap-1.5 font-bold text-amber-700 bg-amber-100 hover:bg-amber-200 px-2.5 py-1 rounded-lg transition-colors cursor-pointer"
                >
                  <FaLock /> Locked (Click to Enter PIN)
                </button>
              )}
            </div>

            {/* Content Textarea */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                Official Terms Content
              </label>
              <textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                className="w-full flex-1 min-h-[260px] p-4 text-xs sm:text-sm font-mono bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none resize-none leading-relaxed"
                placeholder="Enter terms and conditions text..."
              />
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-between border-t border-slate-100 pt-4">
              <span className="text-xs text-slate-400">
                {newContent.length} characters
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setEditingTerm(null)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl text-xs font-bold shadow-md transition-all active:scale-95 disabled:opacity-50"
                >
                  {saving ? (
                    <span>Saving...</span>
                  ) : unlocked ? (
                    <>
                      <FaCheckCircle className="text-xs" />
                      <span>Save Changes</span>
                    </>
                  ) : (
                    <>
                      <FaKey className="text-xs" />
                      <span>Unlock & Save</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Keypad Modal ────────────────────────────────────────────────────── */}
      {showKeypad && (
        <KeypadModal
          onSuccess={unlockEdit}
          onClose={() => setShowKeypad(false)}
          type="secondary"
        />
      )}
    </div>
  );
}
